import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";

import instance from "../../../api/instance";
import parseNumber from "../../../utils/parse-number";
import "react-datepicker/dist/react-datepicker.css";
import { RootState } from "../../../redux/store";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

type ReportType = "0" | "2" | "3" | "4";

interface SportsReportRow {
  eventName: string;
  gameType: string;
  opening: number;
  closing: number;
  profitLoss: number;
}

interface GenericReportRow {
  name: string;
  opening: number;
  closing: number;
  profitLoss: number;
}

interface IClientOption {
  user_id: string;
  user_name: string;
  display_name: string;
  member_type: string;
}

const THIRD_PARTY_ROWS: GenericReportRow[] = [
  { name: "Third Party", opening: 0, closing: 0, profitLoss: 0 },
];

const formatValue = (value: number) => parseNumber({ value, type: "float" }).toFixed(2);
const CLIENT_SEARCH_MIN_LENGTH = 2;
const toAmount = (value: unknown) => Number(parseNumber({ value: value ?? 0, type: "float" }));

const getDefaultDateRange = (): [Date, Date] => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 10);
  return [start, end];
};

const getSportsRowsFromResponse = (payload: any): SportsReportRow[] => {
  const source: any[] = Array.isArray(payload?.sports)
    ? payload.sports
    : Array.isArray(payload?.sport)
      ? payload.sport
      : Array.isArray(payload?.sportsReport)
        ? payload.sportsReport
        : Array.isArray(payload?.sports_report)
          ? payload.sports_report
          : Array.isArray(payload?.sportsData)
            ? payload.sportsData
            : [];

  return source.map((item) => ({
    eventName: item?.event_name ?? item?.eventName ?? item?.game_name ?? item?.name ?? "-",
    gameType: item?.game_type ?? item?.gameType ?? item?.type ?? "-",
    opening: toAmount(item?.opening ?? item?.opening_balance),
    closing: toAmount(item?.closing ?? item?.closing_balance),
    profitLoss: toAmount(item?.profit_loss ?? item?.pnl ?? item?.pl),
  }));
};

const getCasinoRowsFromResponse = (payload: any): GenericReportRow[] => {
  const source: any[] = Array.isArray(payload?.casino)
    ? payload.casino
    : Array.isArray(payload?.casinoReport)
      ? payload.casinoReport
      : Array.isArray(payload?.casino_report)
        ? payload.casino_report
        : Array.isArray(payload?.casinoData)
          ? payload.casinoData
          : [];

  return source.map((item) => ({
    name: item?.casino_name ?? item?.casinoName ?? item?.name ?? item?.game_name ?? "-",
    opening: toAmount(item?.opening ?? item?.opening_balance),
    closing: toAmount(item?.closing ?? item?.closing_balance),
    profitLoss: toAmount(item?.profit_loss ?? item?.pnl ?? item?.pl),
  }));
};

const TotalProfitLossPage = () => {
  const loggedInUserId = useSelector((state: RootState) => state.user.userData?.user_id ?? "");
  const [clientSearchText, setClientSearchText] = useState("");
  const [clientOptions, setClientOptions] = useState<IClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientOption | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => getDefaultDateRange());
  const [reportType, setReportType] = useState<ReportType>("0");
  const [searchText, setSearchText] = useState("");
  const [sportsSourceRows, setSportsSourceRows] = useState<SportsReportRow[]>([]);
  const [casinoSourceRows, setCasinoSourceRows] = useState<GenericReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, endDate] = dateRange;

  useSyncGlobalPageLoader(loading);
  const clientWrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSearchRequestIdRef = useRef(0);

  const query = searchText.trim().toLowerCase();

  const sportsRows = useMemo(() => {
    if (!query) return sportsSourceRows;
    return sportsSourceRows.filter((row) =>
      [row.eventName, row.gameType].some((field) => field.toLowerCase().includes(query))
    );
  }, [query, sportsSourceRows]);

  const casinoRows = useMemo(() => {
    if (!query) return casinoSourceRows;
    return casinoSourceRows.filter((row) => row.name.toLowerCase().includes(query));
  }, [query, casinoSourceRows]);

  const thirdPartyRows = useMemo(() => {
    if (!query) return THIRD_PARTY_ROWS;
    return THIRD_PARTY_ROWS.filter((row) => row.name.toLowerCase().includes(query));
  }, [query]);

  const sportsTotal = useMemo(
    () => sportsRows.reduce((sum, row) => sum + row.profitLoss, 0),
    [sportsRows]
  );
  const casinoTotal = useMemo(
    () => casinoRows.reduce((sum, row) => sum + row.profitLoss, 0),
    [casinoRows]
  );
  const thirdPartyTotal = useMemo(
    () => thirdPartyRows.reduce((sum, row) => sum + row.profitLoss, 0),
    [thirdPartyRows]
  );

  const showSports = reportType === "0" || reportType === "2";
  const showCasino = reportType === "0" || reportType === "3";
  const showThirdParty = reportType === "0" || reportType === "4";

  const handleLoad = async (e: FormEvent) => {
    e.preventDefault();
    if (!loggedInUserId) return;

    // Only call API when an explicit user option is selected.
    if (!selectedClient?.user_id || selectedClient.member_type !== "user") {
      setSportsSourceRows([]);
      setCasinoSourceRows([]);
      return;
    }

    if (reportType === "4") {
      setSportsSourceRows([]);
      setCasinoSourceRows([]);
      return;
    }

    const params: Record<string, string | number> = { userId: selectedClient.user_id };

    if (startDate) {
      const from = new Date(startDate);
      from.setHours(0, 0, 0, 0);
      params.startDate = Math.floor(from.getTime() / 1000);
    }
    if (endDate) {
      const to = new Date(endDate);
      to.setHours(23, 59, 59, 999);
      params.endDate = Math.floor(to.getTime() / 1000);
    }

    if (reportType === "2") params.type = "sports";
    if (reportType === "3") params.type = "casino";

    setLoading(true);
    try {
      const { data } = await instance.get("admin/game-profit-loss-by-grouping", { params });
      const payload = data?.data ?? {};
      const sports = getSportsRowsFromResponse(payload);
      const casino = getCasinoRowsFromResponse(payload);

      if (reportType === "2") {
        setSportsSourceRows(sports);
        setCasinoSourceRows([]);
      } else if (reportType === "3") {
        setSportsSourceRows([]);
        setCasinoSourceRows(casino);
      } else {
        setSportsSourceRows(sports);
        setCasinoSourceRows(casino);
      }
    } catch {
      setSportsSourceRows([]);
      setCasinoSourceRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = useCallback(
    async (queryValue: string) => {
      if (!loggedInUserId) {
        return [] as IClientOption[];
      }
      const requestId = ++clientSearchRequestIdRef.current;
      try {
        const { data } = await instance.get("admin/downline-members", {
          params: {
            adminId: loggedInUserId,
            scope: "recursive",
            search: queryValue,
          },
        });
        const rawList: any[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.data?.rows)
            ? data.data.rows
            : Array.isArray(data?.data?.members)
              ? data.data.members
              : [];
        const list: IClientOption[] = rawList
          .map((item) => ({
            user_id: item?.user_id ?? item?.admin_id ?? item?.id ?? "",
            user_name: item?.user_name ?? item?.username ?? "",
            display_name:
              item?.display_name ?? item?.displayName ?? item?.user_name ?? item?.username ?? "",
            member_type: String(item?.member_type ?? item?.type ?? item?.user_type ?? "").toLowerCase(),
          }))
          .filter((item) => item.user_id);
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return list;
      } catch {
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return [] as IClientOption[];
      }
    },
    [loggedInUserId]
  );

  const handleClientSearchChange = (value: string) => {
    setClientSearchText(value);
    if (selectedClient) {
      setSelectedClient(null);
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = value.toLowerCase().trim();
      if (q.length < CLIENT_SEARCH_MIN_LENGTH) {
        setClientOptions([]);
        setShowClientDropdown(true);
        return;
      }
      const source = await fetchClients(q);
      setClientOptions(source);
      setShowClientDropdown(true);
    }, 200);
  };

  const handleSelectClient = (client: IClientOption) => {
    setSelectedClient(client);
    setClientSearchText(client.display_name || client.user_name);
    setShowClientDropdown(false);
  };

  const handleReset = () => {
    setClientSearchText("");
    setClientOptions([]);
    setSelectedClient(null);
    setShowClientDropdown(false);
    setDateRange(getDefaultDateRange());
    setReportType("0");
    setSearchText("");
    setSportsSourceRows([]);
    setCasinoSourceRows([]);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientWrapperRef.current && !clientWrapperRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="listing-grid report-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Total Profit Loss</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="report-form mb-3">
                <form method="post" className="ajaxFormSubmit" onSubmit={handleLoad}>
                  <div className="row row5">
                    <div className="col-lg-3">
                      <div
                        className="form-group user-lock-search"
                        style={{ position: "relative" }}
                        ref={clientWrapperRef}
                      >
                        <label htmlFor="clientName">Search By Client Name</label>
                        <input
                          id="clientName"
                          type="text"
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="Select option"
                          className="form-control"
                          value={clientSearchText}
                          onChange={(e) => handleClientSearchChange(e.target.value)}
                          onFocus={() => {
                            setClientOptions([]);
                            clientSearchRequestIdRef.current += 1;
                            setShowClientDropdown(true);
                          }}
                        />
                        {showClientDropdown && (
                          <ul
                            className="list-group"
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              maxHeight: "300px",
                              overflowY: "auto",
                              boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                            }}
                          >
                            {clientOptions.length === 0 ? (
                              <li className="list-group-item">
                                {clientSearchText.trim()
                                  ? "No elements found. Consider changing the search query."
                                  : "List is empty."}
                              </li>
                            ) : (
                              clientOptions.map((client) => (
                                <li key={client.user_id} className="list-group-item list-group-item-action">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-left w-100"
                                    style={{ textDecoration: "none", color: "inherit" }}
                                    onClick={() => handleSelectClient(client)}
                                  >
                                    {client.display_name || client.user_name}
                                  </button>
                                </li>
                              ))
                            )}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-3">
                      <label htmlFor="dateRange">Select Date Range</label>
                      <div className="mb-3 mx-datepicker mx-datepicker-range">
                        <DatePicker
                          id="dateRange"
                          name="date"
                          selectsRange
                          startDate={startDate}
                          endDate={endDate}
                          onChange={(update) => setDateRange(update as [Date | null, Date | null])}
                          isClearable
                          dateFormat="dd/MM/yyyy"
                          className="mx-input"
                          wrapperClassName="d-block w-100"
                        />
                      </div>
                    </div>

                    <div className="col-lg-2">
                      <div className="form-group">
                        <label htmlFor="type">Type</label>
                        <select
                          id="type"
                          className="form-control"
                          value={reportType}
                          onChange={(e) => setReportType(e.target.value as ReportType)}
                        >
                          <option value="0">All</option>
                          <option value="2">Sports Report</option>
                          <option value="3">Casino Report</option>
                          <option value="4">Third Party Casino Report</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row row5">
                    <div className="col-lg-3">
                      <button type="submit" id="loaddata" className="btn btn-primary">
                        {loading ? "Loading..." : "Load"}
                      </button>{" "}
                      <button type="button" id="reset" className="btn btn-light" onClick={handleReset}>
                        Reset
                      </button>{" "}
                      <button type="button" className="btn btn-success" aria-label="Export Excel">
                        <i className="fas fa-file-excel" />
                      </button>{" "}
                      <button type="button" className="btn btn-danger" aria-label="Export PDF">
                        <i className="fas fa-file-pdf" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="row">
                <div className="col-6" />
                <div className="col-6 text-right">
                  <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      <input
                        type="search"
                        placeholder="Search..."
                        className="form-control form-control-sm ml-2 form-control"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="table-responsive mb-0">
                {showSports && (
                  <div className="mb-4">
                    <h4>Sports Report</h4>
                    <div className="table no-footer table-hover table-responsive-sm">
                      <table role="table" aria-busy="false" aria-colcount={5} className="table b-table table-bordered">
                        <thead>
                          <tr role="row">
                            <th role="columnheader" scope="col" aria-colindex={1} className="position-relative">
                              <div>Event Name</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={2} className="position-relative">
                              <div>Game Type</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={3} className="position-relative text-right">
                              <div>Opening</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={4} className="position-relative text-right">
                              <div>Closing</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={5} className="position-relative text-right">
                              <div>Profit/Loss</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sportsRows.length === 0 ? (
                            <tr role="row" className="b-table-empty-row">
                              <td colSpan={5}>
                                <div role="alert" aria-live="polite">
                                  <div className="text-center my-2">There are no records to show</div>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            sportsRows.map((row, idx) => (
                              <tr key={`${row.eventName}-${idx}`} role="row" className="nocursor">
                                <td aria-colindex={1}>
                                  <div>{row.eventName}</div>
                                </td>
                                <td aria-colindex={2}>{row.gameType}</td>
                                <td aria-colindex={3}>
                                  <div className="text-right">{formatValue(row.opening)}</div>
                                </td>
                                <td aria-colindex={4}>
                                  <div className="text-right">{formatValue(row.closing)}</div>
                                </td>
                                <td aria-colindex={5}>
                                  <div className="text-right">{formatValue(row.profitLoss)}</div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={4} className="text-right">
                              <strong>Total Profit/Loss</strong>
                            </td>
                            <td className="text-right">
                              <strong>{formatValue(sportsTotal)}</strong>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {showCasino && (
                  <div className="mb-4">
                    <h4>Casino Report</h4>
                    <div className="table table-hover table-responsive-sm">
                      <table role="table" aria-busy="false" aria-colcount={4} className="table b-table table-bordered">
                        <thead>
                          <tr role="row">
                            <th role="columnheader" scope="col" aria-colindex={1} className="position-relative">
                              <div>Casino Name</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={2} className="position-relative text-right">
                              <div>Opening</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={3} className="position-relative text-right">
                              <div>Closing</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={4} className="position-relative text-right">
                              <div>Profit/Loss</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {casinoRows.length === 0 ? (
                            <tr role="row" className="b-table-empty-row">
                              <td colSpan={4}>
                                <div role="alert" aria-live="polite">
                                  <div className="text-center my-2">There are no records to show</div>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            casinoRows.map((row, idx) => (
                              <tr key={`${row.name}-${idx}`} role="row" className="nocursor">
                                <td aria-colindex={1}>
                                  <div>{row.name}</div>
                                </td>
                                <td aria-colindex={2}>
                                  <div className="text-right">{formatValue(row.opening)}</div>
                                </td>
                                <td aria-colindex={3}>
                                  <div className="text-right">{formatValue(row.closing)}</div>
                                </td>
                                <td aria-colindex={4}>
                                  <div className="text-right">{formatValue(row.profitLoss)}</div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={3} className="text-right">
                              <strong>Total Profit/Loss</strong>
                            </td>
                            <td className="text-right">
                              <strong>{formatValue(casinoTotal)}</strong>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {showThirdParty && (
                  <div>
                    <h4>Third Party Report</h4>
                    <div className="table no-footer table-hover table-responsive-sm">
                      <table role="table" aria-busy="false" aria-colcount={4} className="table b-table table-bordered">
                        <thead>
                          <tr role="row">
                            <th role="columnheader" scope="col" aria-colindex={1} className="position-relative">
                              <div>Third Party Name</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={2} className="position-relative text-right">
                              <div>Opening</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={3} className="position-relative text-right">
                              <div>Closing</div>
                            </th>
                            <th role="columnheader" scope="col" aria-colindex={4} className="position-relative text-right">
                              <div>Profit/Loss</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {thirdPartyRows.map((row, idx) => (
                            <tr key={`${row.name}-${idx}`} role="row" className="nocursor">
                              <td aria-colindex={1}>
                                <div>{row.name}</div>
                              </td>
                              <td aria-colindex={2}>
                                <div className="text-right">{formatValue(row.opening)}</div>
                              </td>
                              <td aria-colindex={3}>
                                <div className="text-right">{formatValue(row.closing)}</div>
                              </td>
                              <td aria-colindex={4}>
                                <div className="text-right">{formatValue(row.profitLoss)}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={3} className="text-right">
                              <strong>Total Profit/Loss</strong>
                            </td>
                            <td className="text-right">
                              <strong>{formatValue(thirdPartyTotal)}</strong>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TotalProfitLossPage;
